import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BellRing, ChevronRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { NotificationCategoryIcon } from '@/components/notifications/notification-category-icon';
import { useRootSelector } from '@/hooks/selector.hook';
import { useNotificationFeedService } from '@/services/notification-feed.service';
import { formatRelativeTime } from '@/utils/notification';

const bellBoxClass = 'h-[3.8rem] w-[3.8rem] gap-0 rounded-[1rem] border-0 bg-[#F5F5F5] p-0 outline-none shadow-[0px_1px_2.3px_0px_#0000002E] focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-[#353535] dark:shadow-none';

export default function NotificationsDropdown() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const service = useNotificationFeedService();
  const { items, loaded } = useRootSelector((state) => state.notificationFeed);
  const loading = useRootSelector((state) => Boolean(state.loading.box['get-notification-feed']));
  const visibleItems = items.slice(0, 8);
  const hasUnread = items.some((notification) => !notification.isRead);

  useEffect(() => {
    if (!loaded && location.pathname !== '/notifications') void service.getFeed(1, 20, false);
  }, [loaded, location.pathname, service]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen && !loading && location.pathname !== '/notifications') {
      void service.getFeed(1, 20, false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="second" size="sz38" aria-label="Notifications" className={`relative ${bellBoxClass}`}>
          <svg
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="!h-[2rem] !w-[2rem] text-black dark:text-white"
            aria-hidden="true"
          >
            <path d="M8.72554 19.0695C7.08335 19.0695 5.74609 17.7331 5.74609 16.09C5.74609 15.7611 6.01305 15.4941 6.34198 15.4941C6.67091 15.4941 6.93787 15.7611 6.93787 16.09C6.93787 17.0761 7.74034 17.8777 8.72554 17.8777C9.71073 17.8777 10.5132 17.0761 10.5132 16.09C10.5132 15.7611 10.7802 15.4941 11.1091 15.4941C11.438 15.4941 11.705 15.7611 11.705 16.09C11.705 17.7331 10.3679 19.0695 8.72554 19.0695Z" fill="currentColor" />
            <path d="M15.2812 16.6851H2.17161C1.40493 16.6851 0.78125 16.0614 0.78125 15.2948C0.78125 14.8878 0.958445 14.5026 1.26745 14.238C1.28738 14.2205 1.30876 14.2047 1.33102 14.1903C2.49734 13.1725 3.1648 11.7082 3.1648 10.1621V7.94537C3.1648 4.87864 5.66038 2.38379 8.72638 2.38379C8.85353 2.38379 8.99101 2.38612 9.11816 2.40765C9.44302 2.46162 9.6624 2.76917 9.60829 3.0933C9.55431 3.41743 9.24124 3.63681 8.92264 3.58269C8.85906 3.57237 8.78923 3.57557 8.72638 3.57557C6.31751 3.57557 4.35658 5.53563 4.35658 7.94537V10.1621C4.35658 12.0817 3.51512 13.898 2.05013 15.1445C2.0382 15.1541 2.02787 15.1628 2.01507 15.1715C1.99369 15.1986 1.97303 15.2399 1.97303 15.2948C1.97303 15.4027 2.06366 15.4933 2.17161 15.4933H15.2812C15.3892 15.4933 15.4799 15.4027 15.4799 15.2948C15.4799 15.239 15.4592 15.1986 15.437 15.1715C15.425 15.1628 15.4147 15.1541 15.4028 15.1445C13.9369 13.8971 13.0963 12.0817 13.0963 10.1621V9.29615C13.0963 8.96722 13.3633 8.70027 13.6922 8.70027C14.0211 8.70027 14.2881 8.96722 14.2881 9.29615V10.1621C14.2881 11.7091 14.9563 13.1741 16.1242 14.1927C16.1456 14.207 16.1663 14.2221 16.1853 14.2389C16.4945 14.5026 16.6717 14.8878 16.6717 15.2948C16.6717 16.0614 16.048 16.6851 15.2812 16.6851Z" fill="currentColor" />
            {hasUnread ? (
              <circle cx="14.289" cy="3.97264" r="3.97264" fill="#EF4444" />
            ) : (
              <path d="M14.289 7.94513C12.0985 7.94513 10.3164 6.16314 10.3164 3.97264C10.3164 1.78214 12.0985 0 14.289 0C16.4795 0 18.2615 1.78214 18.2615 3.97264C18.2615 6.16314 16.4795 7.94513 14.289 7.94513ZM14.289 1.19178C12.7555 1.19178 11.5082 2.43913 11.5082 3.97264C11.5082 5.506 12.7555 6.75336 14.289 6.75336C15.8224 6.75336 17.0698 5.506 17.0698 3.97264C17.0698 2.43913 15.8224 1.19178 14.289 1.19178Z" fill="currentColor" />
            )}
          </svg>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[calc(100vw-1.6rem)] overflow-hidden rounded-[1.4rem] border border-[#E9EDF0] bg-main p-0 shadow-[0_0.8rem_2.4rem_rgba(16,24,40,0.12)] sm:w-[36rem] dark:border-[#3A3A3A] dark:shadow-black/30"
      >
        <div className="flex min-h-[4.4rem] items-center px-[1.4rem]">
          <span className="text-[1.35rem] font-medium text-main">Notifications</span>
        </div>
        <DropdownMenuSeparator className="my-0 bg-[#E9EDF0] dark:bg-[#3A3A3A]" />
        <div className="drawer-scrollbar max-h-[36rem] space-y-[0.6rem] overflow-y-auto p-[0.8rem]">
          {loading && !loaded ? (
            <div className="space-y-[1rem] px-[1.6rem] py-[1.6rem]">
              <Skeleton className="h-[4.8rem] w-full" />
              <Skeleton className="h-[4.8rem] w-full" />
            </div>
          ) : null}
          {!loading && loaded && visibleItems.length === 0 ? (
            <div className="flex min-h-[15rem] flex-col items-center justify-center px-[2.4rem] py-[2.8rem] text-center">
              <BellRing className="mb-[1.2rem] h-[2.8rem] w-[2.8rem] text-disable" strokeWidth={1.5} />
              <p className="text-[1.4rem] font-medium text-main">You're all caught up</p>
              <p className="mt-[0.4rem] max-w-[24rem] text-[1.2rem] leading-[1.7rem] text-second">
                New account activity will appear here.
              </p>
            </div>
          ) : null}
          {visibleItems.map((notification) => (
            <Button
              key={notification.notificationId}
              type="button"
              variant="second"
              align="start"
              contentClassName="w-full !items-start !gap-[1rem]"
              onClick={() => { if (!notification.isRead) void service.markAsRead(notification.notificationId); }}
              className={`h-auto w-full rounded-[0.8rem] border p-[1rem] text-left transition-colors hover:bg-secondary ${notification.isRead ? 'border-[#E9EDF0] bg-main hover:border-[#D6DDDB] dark:border-[#3A3A3A] dark:hover:border-[#4A4A4A]' : 'border-[#57E8BA]/60 bg-[#57E8BA]/[0.08] hover:border-[#57E8BA] hover:bg-[#57E8BA]/[0.12] dark:border-[#57E8BA]/40 dark:bg-[#57E8BA]/[0.06] dark:hover:bg-[#57E8BA]/[0.1]'}`}
            >
              <NotificationCategoryIcon category={notification.category} unread={!notification.isRead} compact />
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-start justify-between gap-[1rem]">
                  <span className={`min-w-0 flex-1 truncate text-[1.25rem] leading-[1.7rem] text-main ${notification.isRead ? 'font-normal' : 'font-medium'}`}>{notification.subject || notification.eventType}</span>
                  <span className="shrink-0 whitespace-nowrap pt-[0.1rem] text-[1rem] leading-[1.5rem] text-disable">{formatRelativeTime(notification.createdAt)}</span>
                </span>
                {notification.body ? <span className="mt-[0.15rem] block truncate text-[1.15rem] font-normal leading-[1.6rem] text-second">{notification.body}</span> : null}
              </span>
            </Button>
          ))}
        </div>
        <DropdownMenuSeparator className="my-0 !h-px bg-[#E9EDF0]/60 dark:bg-white/[0.06]" />
        <Link
          to="/notifications"
          onClick={() => setOpen(false)}
          className="flex min-h-[4rem] w-full items-center justify-center gap-[0.4rem] px-[1.4rem] text-[1.2rem] font-medium text-interactive transition-colors hover:bg-secondary"
        >
          View all notifications
          <ChevronRight className="h-[1.4rem] w-[1.4rem]" strokeWidth={1.8} />
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
